import BaseAPIClient from './base/BaseAPIClient';
import ListResponseModel from '../models/ListResponseModel';
import TaskModel from '../models/TaskModel';
import VerifiedTaskTemplateModel from '../models/VerifiedTaskTemplateModel';
import { TemplateLibraryUiSettingsModel } from '../models/UiSettingsModels';
import { taskWorkTypeOptions } from '../constants/options';
import ScheduleModel from '../models/ScheduleModel';

export default class TemplateLibraryClient extends BaseAPIClient {
  getVerifiedTemplates({
    search,
    ordering,
    limit,
    offset,
    categories = [],
    types,
    schedules,
    created_by_company_ids = [],
    archived = false,
  }) {
    const params = {
      search,
      ordering,
      limit,
      offset,
      archived,
    };

    if (categories?.length) {
      params.category__in = categories;
    }

    if (types) {
      params.type__in = types;
    }

    if (schedules) {
      params.schedule__in = schedules;
    }

    if (created_by_company_ids?.length) {
      params.created_by_company_ids = created_by_company_ids.join(',');
    }

    const uri = 'template_library/tasks/verified/';

    return this._get(uri, params).then(t => new ListResponseModel(t, VerifiedTaskTemplateModel));
  }

  addVerifiedTemplateToLibrary(id, categoryOverride) {
    let body = {};
    if (categoryOverride) {
      body = { category: categoryOverride };
    }

    const uri = `template_library/tasks/verified/${id}/add/`;
    return this._post(uri, body).then(t => new VerifiedTaskTemplateModel(t));
  }

  addTaskFromVerifiedTemplate(id, categoryOverride, {
    project,
    equipment,
    location,
    sensor,
    reservation,
    vehicle,
  }) {
    const body = {
      project,
      equipment,
      location,
      sensor,
      reservation,
      vehicle,
    };

    if (categoryOverride) {
      body.category = categoryOverride;
    }
    const uri = `template_library/tasks/verified/${id}/task/`;
    return this._post(uri, body).then(t => new TaskModel(t));
  }

  getVerifiedTemplate(id) {
    const uri = `template_library/tasks/verified/${id}/`;
    return this._get(uri).then(t => new VerifiedTaskTemplateModel(t));
  }

  getTemplateCounts() {
    return this._get('template_library/tasks/counts/');
  }

  getCreatedByFilters(type = 'library') {
    return this._get('template_library/tasks/creators/select_ui/', { type });
  }

  getLibraryUiSettings() {
    const scheduleTypeOptions = Object.entries(ScheduleModel.ScheduleTypeBase).map(([key, value]) => ({
      id: value,
      display_label: key,
    }));
    return this._get('template_library/tasks/company/ui_settings/').then(settings => new TemplateLibraryUiSettingsModel({
      ...settings,
      schedules: (settings.schedules ?? [])
        .map(id => scheduleTypeOptions.find(o => o.id === id))
        .filter(Boolean),
      types: (settings.types ?? [])
        .map(id => taskWorkTypeOptions.find(o => o.id === id))
        .filter(Boolean),
    }));
  }

  updateLibraryUiSettings({
    search = null,
    types = null,
    schedules = null,
    categories = null,
    created_by = null,
  }) {
    const data = {
      search,
      created_by,
    };

    if (!categories?.length) {
      data.categories = [];
    } else {
      data.categories = categories.map(c => c.id);
    }

    if (!schedules?.length) {
      data.schedules = [];
    } else {
      data.schedules = schedules.map(c => c.id);
    }

    if (!types?.length) {
      data.types = [];
    } else {
      data.types = types.map(c => c.id);
    }
    return this._patch(
      'template_library/tasks/company/ui_settings/',
      data,
    ).then(settings => new TemplateLibraryUiSettingsModel(settings));
  }

  getVerifiedUiSettings() {
    const scheduleTypeOptions = Object.entries(ScheduleModel.ScheduleTypeBase).map(([key, value]) => ({
      id: value,
      display_label: key,
    }));
    return this._get('template_library/tasks/verified/ui_settings/').then(settings => new TemplateLibraryUiSettingsModel({
      ...settings,
      schedules: (settings.schedules ?? [])
        .map(id => scheduleTypeOptions.find(o => o.id === id))
        .filter(Boolean),
      types: (settings.types ?? [])
        .map(id => taskWorkTypeOptions.find(o => o.id === id))
        .filter(Boolean),
    }));
  }

  updateVerifiedUiSettings({
    search = null,
    types = null,
    schedules = null,
    categories = null,
    created_by = null,
  }) {
    const data = {
      search,
      created_by,
    };
    if (!categories?.length) {
      data.categories = [];
    } else {
      data.categories = categories.map(c => c.id || c);
    }

    if (!schedules?.length) {
      data.schedules = [];
    } else {
      data.schedules = schedules.map(s => s.id || s);
    }

    if (!types?.length) {
      data.types = [];
    } else {
      data.types = types.map(t => t.id || t);
    }
    return this._patch(
      'template_library/tasks/verified/ui_settings/',
      data,
    ).then(settings => new TemplateLibraryUiSettingsModel(settings));
  }

  recordVerifiedImpressions(templateIds) {
    return this._post('template_library/tasks/verified/impressions/', { template_ids: templateIds });
  }
}
